/**
 * Timeline grouping utilities
 * PROJ-024 - Fase 1: Quick Wins
 */

import { format, isToday, isYesterday, isThisWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import type { TimelineEvent } from '@/types/timeline';

/**
 * Agrupa eventos por día con etiquetas inteligentes
 *
 * Etiquetas generadas:
 * - "Hoy" para eventos de hoy
 * - "Ayer" para eventos de ayer
 * - "Lunes", "Martes", etc. para esta semana
 * - "15 de noviembre de 2025" para fechas anteriores
 *
 * @param events - Array de eventos del timeline
 * @returns Map con grupos de eventos por etiqueta temporal
 *
 * @example
 * const groups = groupEventsByDay(events);
 * groups.get('Hoy'); // [event1, event2, ...]
 */
export function groupEventsByDay(
  events: TimelineEvent[]
): Map<string, TimelineEvent[]> {
  const groups = new Map<string, TimelineEvent[]>();

  events.forEach(event => {
    const date = new Date(event.at);
    let label: string;

    if (isToday(date)) {
      label = 'Hoy';
    } else if (isYesterday(date)) {
      label = 'Ayer';
    } else if (isThisWeek(date, { weekStartsOn: 1 })) {
      // Semana empieza el lunes (1)
      label = format(date, 'EEEE', { locale: es }); // "Lunes", "Martes", etc.
    } else {
      label = format(date, 'PPP', { locale: es }); // "15 de noviembre de 2025"
    }

    if (!groups.has(label)) {
      groups.set(label, []);
    }
    groups.get(label)!.push(event);
  });

  return groups;
}

/**
 * Obtiene el orden de los grupos (más reciente primero)
 *
 * @param groups - Map de grupos generado por groupEventsByDay
 * @returns Array de labels ordenados
 */
export function getGroupOrder(groups: Map<string, TimelineEvent[]>): string[] {
  const daysOfWeek = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

  return Array.from(groups.keys()).sort((a, b) => {
    // "Hoy" siempre primero
    if (a === 'Hoy') return -1;
    if (b === 'Hoy') return 1;

    // "Ayer" segundo
    if (a === 'Ayer') return -1;
    if (b === 'Ayer') return 1;

    // Días de la semana en orden
    const aIndex = daysOfWeek.indexOf(a);
    const bIndex = daysOfWeek.indexOf(b);
    if (aIndex !== -1 && bIndex !== -1) {
      // Más reciente primero (orden inverso)
      return bIndex - aIndex;
    }

    // Fechas completas: parsear y comparar
    try {
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateB.getTime() - dateA.getTime(); // Más reciente primero
    } catch {
      // Si no se puede parsear, mantener orden actual
      return 0;
    }
  });
}
